<template>
  <div>
    <div v-if="isFetchingState(state)">
      <t-skeleton />
    </div>
    <div v-else-if="isErrorState(state)">
      <t-empty>
        <template #title>
          {{ $t("organisation-id-not-provided") }}
        </template>
      </t-empty>
    </div>
    <div v-else-if="!isFetchingState(state)">
      <authorisation-wrapper
        :resource="EMAIL_SECURITY"
        :action="WRITE"
        v-slot="{ isAllowed: isWriteAllowed }"
      >
        <div class="tw-flex tw-flex-col tw-gap-8">
          <div>
            <h2 class="tw-mb-6">{{ t("remediation-lead-in") }}</h2>
            <cl-radio-buttons
              class="tw-ml-4 tw-mb-2"
              v-model="selectedIsAuto"
              id="radio-buttons"
              test-id="radio-buttons"
            >
              <cl-radio-button
                @click:selected="
                  (newValue: boolean) => (selectedIsAuto = newValue)
                "
                :value="false"
                :testId="`${testId}-radio-manual`"
                id="manual"
                :readonly="!isWriteAllowed"
              >
                <div class="tw-ml-4">
                  <div>
                    {{ $t("manual-remediation-radio") }}
                  </div>
                  <div class="tw-text-sm">
                    {{ $t("manual-remediation-hint") }}
                  </div>
                </div>
              </cl-radio-button>
              <cl-radio-button
                @update:selected="
                  (newValue: boolean) => (selectedIsAuto = newValue)
                "
                :value="true"
                :testId="`${testId}-radio-auto`"
                id="remediation-radio-auto"
                :readonly="!isWriteAllowed"
              >
                <div class="tw-ml-4">
                  <div v-if="isMspRoute">
                    {{ $t("auto-remediation") }}
                  </div>
                  <div v-else>
                    {{ $t("auto-remediation") }}
                  </div>
                  <div v-if="isMspRoute" class="tw-text-sm">
                    {{ $t("auto-remediation-radio-button") }}
                  </div>
                  <div v-else class="tw-text-sm">
                    {{ $t("auto-remediation-radio-button") }}
                  </div>
                </div>
              </cl-radio-button>
            </cl-radio-buttons>
          </div>
          <div>
            <h2 class="tw-mb-6">
              {{ t("choose-remediation-folder-lead-in") }}
            </h2>
            <cl-radio-buttons
              class="tw-ml-4"
              v-model="selectedToJunk"
              id="radio-buttons"
              test-id="radio-buttons"
            >
              <cl-radio-button
                @update:selected="selectedToJunk = $event"
                :value="true"
                :testId="`${testId}-radio-junk`"
                id="junk"
                :readonly="!isWriteAllowed"
              >
                <div class="tw-ml-4">
                  {{ $t("remediate-to-junk-folder") }}
                </div>
              </cl-radio-button>
              <cl-radio-button
                @update:selected="selectedToJunk = $event"
                :value="false"
                :testId="`${testId}-radio-deleted`"
                id="remediation-radio-deleted"
                :readonly="!isWriteAllowed"
              >
                <div class="tw-ml-4">
                  {{ $t("remediate-to-deleted-folder") }}
                </div>
              </cl-radio-button>
            </cl-radio-buttons>
          </div>
        </div>
      </authorisation-wrapper>
      <div class="tw-flex tw-justify-end tw-gap-2">
        <cl-button
          v-if="settingsChanged"
          variant="secondary-ghost"
          :data-test-id="`${testId}-cancel`"
          :disabled="isUpdatingState(state) || isFetchingState(state)"
          @on-click="setDefaultStatus"
        >
          {{ $t("cancel") }}
        </cl-button>
        <cl-button
          variant="secondary"
          :data-test-id="`${testId}-save`"
          :disabled="isSaveButtonDisabled"
          @on-click="openConfirmationModal"
        >
          {{ $t("save") }}
        </cl-button>
      </div>
    </div>
    <remediation-settings-confirm
      v-if="isEditState(state) || isUpdatingState(state)"
      :is-auto="state.isAuto"
      :is-to-junk="state.isToJunk"
      :loading="isUpdatingState(state)"
      :data-test-id="`${testId}-confirmation-modal`"
      @on-close="setState({ type: 'READY' })"
      @on-confirm="editSettingsStatus"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import RemediationSettingsConfirm from "./RemediationSettingsConfirm.vue";
import { useRemediationSettingsState } from "./useRemediationSettingsState";
import { useGlobalOrganisationIDBasedOnRoute } from "@/composables/useGlobalOrganisationIDBasedOnRoute";
import TSkeleton from "@/components/TSkeleton";
import { useIsMspRoute } from "@/composables/useIsMspRoute";
import { AuthorisationWrapper, EMAIL_SECURITY, WRITE } from "@/authorisation";
import { useToastStore } from "@/components/TToasts/toast.store.ts";
import { useRemediation } from "./useRemediation";
import TEmpty from "@/components/TEmpty";

const toastStore = useToastStore();
const {
  remediation,
  getAutoRemediation,
  updateAutoRemediation,
  getRemediateToJunk,
  updateRemediateToJunk,
} = useRemediation();

const { t } = useI18n();

const {
  isFetchingState,
  isEditState,
  isUpdatingState,
  isErrorState,
  setState,
  state,
} = useRemediationSettingsState();

const { globalOrganisationId: organisationID } =
  useGlobalOrganisationIDBasedOnRoute();

const defaultIsAuto = computed<boolean | undefined>(
  () => remediation.value.isAuto,
);
const defaultIsToJunk = computed<boolean | undefined>(
  () => remediation.value.isToJunk,
);
const selectedIsAuto = ref<boolean | undefined>(undefined);
const selectedToJunk = ref<boolean | undefined>(undefined);

const getAutoRemediationSettings = async () => {
  if (!organisationID.value) {
    setState({ type: "ERROR" });
    return;
  }
  try {
    setState({ type: "FETCHING" });

    await Promise.allSettled([
      getAutoRemediation({
        organisationID: organisationID.value,
      }),
      getRemediateToJunk({
        organisationID: organisationID.value,
      }),
    ]);
  } finally {
    setState({ type: "READY" });
  }
};

getAutoRemediationSettings();

const isAutoChanged = computed(
  () => selectedIsAuto.value !== defaultIsAuto.value,
);
const isToJunkChanged = computed(
  () => selectedToJunk.value !== defaultIsToJunk.value,
);

const settingsChanged = computed(
  () => isAutoChanged.value || isToJunkChanged.value,
);

const isSaveButtonDisabled = computed(
  () =>
    !settingsChanged.value ||
    isUpdatingState(state.value) ||
    isFetchingState(state.value) ||
    typeof selectedIsAuto.value !== "boolean" ||
    typeof selectedToJunk.value !== "boolean",
);

const setDefaultStatus = () => {
  selectedIsAuto.value = defaultIsAuto.value;
  selectedToJunk.value = defaultIsToJunk.value;
};

watch(
  defaultIsAuto,
  (value) => {
    selectedIsAuto.value = value;
  },
  { immediate: true },
);
watch(
  defaultIsToJunk,
  (value) => {
    selectedToJunk.value = value;
  },
  { immediate: true },
);

const openConfirmationModal = () => {
  if (
    typeof selectedIsAuto.value !== "boolean" ||
    typeof selectedToJunk.value !== "boolean"
  ) {
    return;
  }

  setState({
    type: "EDIT",
    isAuto: selectedIsAuto.value,
    isToJunk: selectedToJunk.value,
  });
};

const editSettingsStatus = async () => {
  if (!organisationID.value) {
    return;
  }
  if (!isEditState(state.value)) {
    return;
  }

  const { isAuto: confirmedIsAuto, isToJunk: confirmedIsToJunk } = state.value;
  const shouldUpdateAuto = confirmedIsAuto !== defaultIsAuto.value;
  const shouldUpdateToJunk = confirmedIsToJunk !== defaultIsToJunk.value;

  setState({
    type: "UPDATING",
    isAuto: confirmedIsAuto,
    isToJunk: confirmedIsToJunk,
  });
  try {
    if (shouldUpdateAuto) {
      await updateAutoRemediation({
        organisationID: organisationID.value,
        status: confirmedIsAuto,
      });
    }
    if (shouldUpdateToJunk) {
      await updateRemediateToJunk({
        organisationID: organisationID.value,
        status: confirmedIsToJunk,
      });
    }

    toastStore.displayToast({
      title: t("success"),
      message: t("remediation-settings-updated"),
      duration: 2000,
      variant: "success",
      testId: "remediation-toast-success",
    });
  } catch {
    toastStore.displayToast({
      title: t("error"),
      message: t("error-remediation"),
      duration: 2000,
      variant: "danger",
      testId: "remediation-toast-error",
    });
  } finally {
    setState({ type: "READY" });
  }
};

const { isMspRoute } = useIsMspRoute();
const testId = computed(() =>
  isMspRoute.value ? "msp-remediation" : "customer-remediation",
);
</script>
